import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import api from "../hooks/useApi";

const FILTERS = [
  "All",
  "Residential",
  "Commercial",
  "Government",
  "Renovation",
];

const DEMO_PROJECTS = [
  {
    _id: "1",
    title: "2 BHK Dream Home",
    location: "Nandurbar",
    type: "Residential",
    status: "Completed",
    year: 2023,
    area: "1,400 sq ft",
    description:
      "Beautiful 2BHK with modern interior, rainwater harvesting, and premium tile work.",
    images: [],
  },
  {
    _id: "2",
    title: "Government School Extension",
    location: "Nandurbar Taluka",
    type: "Government",
    status: "Completed",
    year: 2023,
    area: "3,200 sq ft",
    description:
      "3-room extension for government primary school. Completed ahead of schedule.",
    images: [],
  },
  {
    _id: "3",
    title: "3 BHK Family Residence",
    location: "Nandurbar",
    type: "Residential",
    status: "Completed",
    year: 2024,
    area: "2,100 sq ft",
    description:
      "Spacious 3BHK with vastu layout, premium flooring, and artistic ceiling work.",
    images: [],
  },
  {
    _id: "4",
    title: "Commercial Shop Complex",
    location: "Shahada",
    type: "Commercial",
    status: "In Progress",
    year: 2025,
    area: "1,800 sq ft",
    description: "Ground floor shop complex with mezzanine storage.",
    images: [],
  },
  {
    _id: "5",
    title: "Home Renovation Project",
    location: "Nandurbar",
    type: "Renovation",
    status: "Completed",
    year: 2024,
    area: "900 sq ft",
    description:
      "Complete renovation of a 20-year-old home with modern finishes.",
    images: [],
  },
  {
    _id: "6",
    title: "4 BHK Bungalow",
    location: "Nandurbar",
    type: "Residential",
    status: "Completed",
    year: 2024,
    area: "3,500 sq ft",
    description:
      "Luxury 4BHK bungalow with landscaping, premium fittings, and smart home features.",
    images: [],
  },
];

const TYPE_ICONS = {
  Residential: "🏠",
  Commercial: "🏢",
  Government: "🏛️",
  Renovation: "🔧",
  Industrial: "🏭",
};

const fadeUp = {
  hidden: { opacity: 0, y: 25 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [active, setActive] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await api.getProjects();

        console.log("PROJECT API RESPONSE:", res);

        // Handle both cases: array OR { data: [] }
        const data = Array.isArray(res.data) ? res.data : res.data?.data || [];

        if (data.length > 0) {
          setProjects(data);
        } else {
          console.warn("Empty API response, using demo data");
          setProjects(DEMO_PROJECTS);
        }
      } catch (err) {
        console.error("PROJECT FETCH ERROR:", err?.response || err.message);
        setProjects(DEMO_PROJECTS);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const filtered =
    active === "All" ? projects : projects.filter((p) => p.type === active);

  return (
    <>
      <Helmet>
        <title>Projects — NirmalRam Construction</title>
        <meta
          name="description"
          content="View completed and ongoing construction projects by NirmalRam Construction in Nandurbar, Maharashtra. Dream homes, government contracts, and commercial builds."
        />
      </Helmet>

      {/* Header */}
      <section
        style={{
          background: "#0A0A0A",
          padding: "8rem 0 4rem",
          borderBottom: "1px solid rgba(201,168,76,0.12)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div className="grid-bg" />
        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="section-tag">Our Portfolio</span>
            <h1
              style={{
                fontFamily: "Cormorant Garamond,serif",
                marginBottom: "1rem",
              }}
            >
              Projects We're
              <br />
              Proud Of
            </h1>
            <div className="section-divider" />
            <p style={{ color: "#8A8070", maxWidth: "520px", lineHeight: 1.8 }}>
              Every project is a testament to our craftsmanship, attention to
              detail, and commitment to quality. Browse our completed and
              ongoing work.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section
        style={{
          padding: "2rem 0",
          borderBottom: "1px solid rgba(201,168,76,0.08)",
          background: "#0A0A0A",
          position: "sticky",
          top: "70px",
          zIndex: 50,
          backdropFilter: "blur(10px)",
        }}
      >
        <div className="container">
          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setActive(f)}
                style={{
                  padding: "0.5rem 1.25rem",
                  borderRadius: "2px",
                  fontSize: "0.8rem",
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  fontFamily: "Outfit,sans-serif",
                  background: active === f ? "#C9A84C" : "transparent",
                  color: active === f ? "#080808" : "#8A8070",
                  border:
                    active === f
                      ? "1px solid #C9A84C"
                      : "1px solid rgba(201,168,76,0.2)",
                  fontWeight: active === f ? 600 : 400,
                }}
              >
                {f}
              </button>
            ))}
            <span
              style={{
                marginLeft: "auto",
                color: "#5A5450",
                fontSize: "0.85rem",
                alignSelf: "center",
              }}
            >
              {filtered.length} project{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="section">
        <div className="container">
          {loading ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))",
                gap: "1.5rem",
              }}
            >
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  style={{
                    background: "#111",
                    border: "1px solid rgba(201,168,76,0.1)",
                    borderRadius: "3px",
                    overflow: "hidden",
                  }}
                >
                  <div className="skeleton" style={{ height: "220px" }} />
                  <div style={{ padding: "1.25rem" }}>
                    <div
                      className="skeleton"
                      style={{ height: "20px", marginBottom: "0.75rem" }}
                    />
                    <div
                      className="skeleton"
                      style={{ height: "14px", width: "60%" }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))",
                gap: "1.5rem",
              }}
            >
              {filtered.map((p) => (
                <motion.div key={p._id} variants={fadeUp}>
                  <Link
                    to={`/projects/${p._id}`}
                    style={{ textDecoration: "none", display: "block" }}
                  >
                    <motion.div
                      whileHover={{ y: -5 }}
                      style={{
                        background: "#0E0E0E",
                        border: "1px solid rgba(201,168,76,0.12)",
                        borderRadius: "3px",
                        overflow: "hidden",
                        transition: "border-color 0.3s",
                      }}
                    >
                      {/* Thumbnail */}
                      <div
                        style={{
                          height: "220px",
                          background:
                            "linear-gradient(135deg, #1a1208, #0e0905)",
                          position: "relative",
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "3.5rem",
                        }}
                      >
                        {p.images?.[0]?.url ? (
                          <img
                            src={p.images[0].url}
                            alt={p.title}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              position: "absolute",
                              inset: 0,
                            }}
                          />
                        ) : (
                          TYPE_ICONS[p.type] || "🏗️"
                        )}
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            background:
                              "linear-gradient(to top, rgba(8,8,8,0.7) 0%, transparent 60%)",
                          }}
                        />
                        <span
                          className={`badge ${p.status === "Completed" ? "badge-green" : "badge-gold"}`}
                          style={{
                            position: "absolute",
                            top: "0.75rem",
                            right: "0.75rem",
                          }}
                        >
                          {p.status}
                        </span>
                        <span
                          className="badge badge-blue"
                          style={{
                            position: "absolute",
                            top: "0.75rem",
                            left: "0.75rem",
                          }}
                        >
                          {p.type}
                        </span>
                      </div>
                      <div style={{ padding: "1.5rem" }}>
                        <h3
                          style={{
                            fontSize: "1.1rem",
                            marginBottom: "0.5rem",
                            color: "#F0EAE0",
                          }}
                        >
                          {p.title}
                        </h3>
                        <p
                          style={{
                            color: "#5A5450",
                            fontSize: "0.85rem",
                            lineHeight: 1.6,
                            marginBottom: "1rem",
                          }}
                        >
                          {p.description?.slice(0, 90)}
                          {p.description?.length > 90 ? "..." : ""}
                        </p>
                        <div
                          style={{
                            display: "flex",
                            gap: "1.25rem",
                            fontSize: "0.78rem",
                            color: "#C9A84C",
                          }}
                        >
                          <span>📍 {p.location}</span>
                          {p.area && <span>📐 {p.area}</span>}
                          <span>📅 {p.year}</span>
                        </div>
                      </div>
                    </motion.div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* Sitemap of Projects */}
      <section
        style={{
          background: "#0A0A0A",
          borderTop: "1px solid rgba(201,168,76,0.1)",
          padding: "3rem 0",
        }}
      >
        <div className="container">
          <h3
            style={{
              fontFamily: "Cormorant Garamond,serif",
              fontSize: "1.3rem",
              marginBottom: "1.5rem",
              color: "#8A8070",
            }}
          >
            Project Index
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))",
              gap: "0.5rem",
            }}
          >
            {projects.map((p) => (
              <Link
                key={p._id}
                to={`/projects/${p._id}`}
                style={{
                  color: "#5A5450",
                  fontSize: "0.82rem",
                  padding: "0.4rem 0",
                  borderBottom: "1px solid rgba(201,168,76,0.06)",
                  transition: "color 0.2s",
                  display: "block",
                }}
                onMouseEnter={(e) => (e.target.style.color = "#C9A84C")}
                onMouseLeave={(e) => (e.target.style.color = "#5A5450")}
              >
                › {p.title} ({p.year})
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
